import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { BaseEntityNameLess } from '@api/entities/BaseEntity';

type VirtualListItemProps<T extends BaseEntityNameLess> = {
    items: T[];
    children: (args: { entity: T; ind: number }) => React.ReactNode;
};

const VirtualListItem = <T extends BaseEntityNameLess>({
    items,
    children,
}: VirtualListItemProps<T>) => {
    const parentRef = useRef<HTMLDivElement>(null);

    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 60,
        overscan: 5,
    });

    return (
        <div
            ref={parentRef}
            style={{
                height: '100%',
                overflow: 'auto',
            }}
        >
            <div
                style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {virtualizer.getVirtualItems().map(virtualItem => {
                    const entity = items[virtualItem.index];
                    const ind = virtualItem.index;
                    return (
                        <div
                            key={entity.id}
                            data-index={virtualItem.index}
                            ref={virtualizer.measureElement}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${virtualItem.start}px)`,
                            }}
                        >
                            {children({ entity, ind })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default VirtualListItem;
